import React, { useEffect, useMemo } from 'react'
import { useLocation, matchPath } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

type RouteMeta = {
  title: string
  description: string
  noindex: boolean
}

type StaticRouteConfig = {
  titleKey: string
  titleFallback: string
  descriptionKey?: string
  descriptionFallback?: string
  noindex?: boolean
}

type DynamicRouteConfig = {
  pattern: string
  resolve: (params: Record<string, string>, t: TranslationFn, brand: string) => RouteMeta
}

const STATIC_ROUTE_META: Record<string, StaticRouteConfig> = {
  '/': {
    titleKey: 'seo.home',
    titleFallback: 'Digital Insurance Broker',
    descriptionKey: 'seo.descriptions.home',
    descriptionFallback: 'Compare and buy insurance online with Frani digital broker services.',
  },
  '/about-us': {
    titleKey: 'seo.aboutUs',
    titleFallback: 'About Us',
    descriptionKey: 'seo.descriptions.aboutUs',
    descriptionFallback: 'Learn more about Frani and our mission in digital insurance.',
  },
  '/faq': {
    titleKey: 'seo.faq',
    titleFallback: 'FAQ',
    descriptionKey: 'seo.descriptions.faq',
    descriptionFallback: 'Find quick answers to common insurance and platform questions.',
  },
  '/our-partners': {
    titleKey: 'seo.ourPartners',
    titleFallback: 'Our Partners',
  },
  '/privacy': {
    titleKey: 'seo.privacy',
    titleFallback: 'Privacy Policy',
    descriptionKey: 'seo.descriptions.privacy',
    descriptionFallback: 'Read how Frani collects and protects your personal data.',
  },
  '/consent': {
    titleKey: 'seo.consent',
    titleFallback: 'Consent',
    noindex: true,
  },
  '/terms-and-conditions': {
    titleKey: 'seo.termsAndConditions',
    titleFallback: 'Terms and Conditions',
    descriptionKey: 'seo.descriptions.termsAndConditions',
    descriptionFallback: 'Review Frani terms and conditions before using our services.',
  },
  '/login': {
    titleKey: 'seo.login',
    titleFallback: 'Login',
    noindex: true,
  },
  '/reset-password': {
    titleKey: 'seo.resetPassword',
    titleFallback: 'Reset Password',
    noindex: true,
  },
  '/category': {
    titleKey: 'seo.category',
    titleFallback: 'Insurance Categories',
  },
  '/pay/payment-success': {
    titleKey: 'seo.paymentSuccess',
    titleFallback: 'Payment Successful',
    noindex: true,
  },
  '/pay/payment-failure': {
    titleKey: 'seo.paymentFailure',
    titleFallback: 'Payment Failed',
    noindex: true,
  },
  '/business': {
    titleKey: 'seo.business',
    titleFallback: 'Business Insurance',
  },
  '/business-category': {
    titleKey: 'seo.businessCategory',
    titleFallback: 'Business Categories',
  },
  '/dashboard': {
    titleKey: 'seo.dashboard',
    titleFallback: 'Dashboard',
    noindex: true,
  },
  '/my-policies': {
    titleKey: 'seo.myPolicies',
    titleFallback: 'My Policies',
    noindex: true,
  },
  '/transactions': {
    titleKey: 'seo.transactions',
    titleFallback: 'Transactions',
    noindex: true,
  },
  '/profile': {
    titleKey: 'seo.profile',
    titleFallback: 'Profile',
    noindex: true,
  },
  '/security': {
    titleKey: 'seo.security',
    titleFallback: 'Security',
    noindex: true,
  },
  '/notification-list': {
    titleKey: 'seo.notificationList',
    titleFallback: 'Notifications',
    noindex: true,
  },
  '/notification-details': {
    titleKey: 'seo.notificationDetails',
    titleFallback: 'Notification Details',
    noindex: true,
  },
  '/auth/personal-info': {
    titleKey: 'seo.signUpPersonalInfo',
    titleFallback: 'Sign Up Personal Info',
    noindex: true,
  },
  '/auth/create-password': {
    titleKey: 'seo.signUpCreatePassword',
    titleFallback: 'Create Password',
    noindex: true,
  },
}

const DYNAMIC_ROUTE_META: DynamicRouteConfig[] = [
  {
    pattern: '/verify-email/*',
    resolve: (_params, t, brand) => ({
      title: t('seo.emailVerification', { defaultValue: 'Email Verification' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.emailVerification', { defaultValue: 'Email Verification' }),
        brand,
        defaultValue: 'Email Verification - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
  {
    pattern: '/reset-password-create/:token',
    resolve: (_params, t, brand) => ({
      title: t('seo.createNewPassword', { defaultValue: 'Create New Password' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.createNewPassword', { defaultValue: 'Create New Password' }),
        brand,
        defaultValue: 'Create New Password - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
  {
    pattern: '/policies/:encode',
    resolve: (_params, t, brand) => ({
      title: t('seo.policyDocument', { defaultValue: 'Policy Document' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.policyDocument', { defaultValue: 'Policy Document' }),
        brand,
        defaultValue: 'Policy Document - {{brand}} insurance platform.',
      }),
      noindex: false,
    }),
  },
  {
    pattern: '/:type/product-detail',
    resolve: (params, t, brand) => {
      const type = params.type ? segmentToTitle(params.type, t) : ''
      const productDetail = t('seo.productDetail', { defaultValue: 'Product Detail' })
      const title = type ? `${type} - ${productDetail}` : productDetail

      return {
        title,
        description: t('seo.defaultDescriptionTemplate', {
          title,
          brand,
          defaultValue: '{{title}} - Compare and buy insurance online with {{brand}}.',
        }),
        noindex: false,
      }
    },
  },
  {
    pattern: '/travel-policy-detail/:policyId',
    resolve: (_params, t, brand) => ({
      title: t('seo.travelPolicyDetail', { defaultValue: 'Travel Policy Detail' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.travelPolicyDetail', { defaultValue: 'Travel Policy Detail' }),
        brand,
        defaultValue: '{{title}} - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
  {
    pattern: '/tpl-policy-detail/:policyId',
    resolve: (_params, t, brand) => ({
      title: t('seo.tplPolicyDetail', { defaultValue: 'TPL Policy Detail' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.tplPolicyDetail', { defaultValue: 'TPL Policy Detail' }),
        brand,
        defaultValue: '{{title}} - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
  {
    pattern: '/international-tpl-policy-detail/:policyId',
    resolve: (_params, t, brand) => ({
      title: t('seo.internationalTplPolicyDetail', { defaultValue: 'International TPL Policy Detail' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.internationalTplPolicyDetail', { defaultValue: 'International TPL Policy Detail' }),
        brand,
        defaultValue: '{{title}} - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
  {
    pattern: '/welcomer-policy-detail/:policyId',
    resolve: (_params, t, brand) => ({
      title: t('seo.welcomerPolicyDetail', { defaultValue: 'Welcomer Policy Detail' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.welcomerPolicyDetail', { defaultValue: 'Welcomer Policy Detail' }),
        brand,
        defaultValue: '{{title}} - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
  {
    pattern: '/pet-policy-detail/:policyId',
    resolve: (_params, t, brand) => ({
      title: t('seo.petPolicyDetail', { defaultValue: 'Pet Policy Detail' }),
      description: t('seo.defaultDescriptionTemplate', {
        title: t('seo.petPolicyDetail', { defaultValue: 'Pet Policy Detail' }),
        brand,
        defaultValue: '{{title}} - {{brand}} insurance platform.',
      }),
      noindex: true,
    }),
  },
]

const NOINDEX_PATTERNS = [
  '/dashboard',
  '/my-policies',
  '/transactions',
  '/profile',
  '/security',
  '/notification-list',
  '/notification-details',
  '/auth/personal-info',
  '/auth/create-password',
  '/verify-email/*',
  '/reset-password-create/:token',
  '/travel-policy-detail/:policyId',
  '/tpl-policy-detail/:policyId',
  '/international-tpl-policy-detail/:policyId',
  '/welcomer-policy-detail/:policyId',
  '/pet-policy-detail/:policyId',
  '/pay/payment-success',
  '/pay/payment-failure',
]

const segmentToTitle = (segment: string, t: TranslationFn): string => {
  const normalizedSegment = decodeURIComponent(segment).trim()

  if (!normalizedSegment) {
    return ''
  }

  if (
    /^[0-9]+$/.test(normalizedSegment) ||
    normalizedSegment.length > 32 ||
    /^[a-f0-9-]{24,}$/i.test(normalizedSegment)
  ) {
    return ''
  }

  const token = normalizedSegment.toLowerCase().replace(/[^a-z0-9-]+/g, '')
  const key = `seo.segments.${token}`
  const translatedSegment = t(key, { defaultValue: '' })

  if (translatedSegment) {
    return translatedSegment
  }

  return normalizedSegment.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

const shouldNoindexPath = (pathname: string): boolean =>
  NOINDEX_PATTERNS.some((pattern) => {
    if (pattern.includes(':') || pattern.includes('*')) {
      return Boolean(matchPath(pattern, pathname))
    }

    return pattern === pathname
  })

const buildFallbackTitleFromPath = (pathname: string, t: TranslationFn): string => {
  const title = pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => segmentToTitle(segment, t))
    .filter(Boolean)
    .join(' - ')

  return title || t('seo.home', { defaultValue: 'Digital Insurance Broker' })
}

const resolveRouteMeta = (pathname: string, t: TranslationFn): RouteMeta => {
  const brand = t('seo.brand', { defaultValue: 'Frani' })
  const staticMeta = STATIC_ROUTE_META[pathname]

  if (staticMeta) {
    const title = t(staticMeta.titleKey, { defaultValue: staticMeta.titleFallback })
    const description = staticMeta.descriptionKey
      ? t(staticMeta.descriptionKey, {
          defaultValue:
            staticMeta.descriptionFallback ||
            `${title} - ${t('seo.defaultDescription', {
              defaultValue: `Compare and buy insurance online with ${brand}.`,
            })}`,
        })
      : t('seo.defaultDescriptionTemplate', {
          title,
          brand,
          defaultValue: '{{title}} - Compare and buy insurance online with {{brand}}.',
        })

    return {
      title,
      description,
      noindex: Boolean(staticMeta.noindex),
    }
  }

  const dynamicRoute = DYNAMIC_ROUTE_META.find(({ pattern }) => matchPath(pattern, pathname))

  if (dynamicRoute) {
    const match = matchPath(dynamicRoute.pattern, pathname)
    return dynamicRoute.resolve((match?.params as Record<string, string>) ?? {}, t, brand)
  }

  const fallbackTitle = buildFallbackTitleFromPath(pathname, t)

  return {
    title: fallbackTitle,
    description: t('seo.defaultDescriptionTemplate', {
      title: fallbackTitle,
      brand,
      defaultValue: '{{title}} - Compare and buy insurance online with {{brand}}.',
    }),
    noindex: shouldNoindexPath(pathname),
  }
}

const setOrUpdateMeta = (selector: string, attributes: Record<string, string>): void => {
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([attribute, value]) => {
    if (element?.getAttribute(attribute) !== value) {
      element?.setAttribute(attribute, value)
    }
  })
}

const setCanonicalLink = (pathname: string): void => {
  const canonicalHref = `${window.location.origin}${pathname}`
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null

  if (!canonical) {
    canonical = document.createElement('link')
    canonical.rel = 'canonical'
    document.head.appendChild(canonical)
  }

  if (canonical.href !== canonicalHref) {
    canonical.href = canonicalHref
  }
}

const applyFallbackH1 = (title: string): void => {
  const existingH1 = document.querySelector('h1')
  const fallbackId = 'seo-route-h1-fallback'
  const fallbackH1 = document.getElementById(fallbackId)

  if (existingH1 && existingH1.id !== fallbackId) {
    if (fallbackH1) {
      fallbackH1.remove()
    }
    return
  }

  const root = document.getElementById('root')

  if (!root) {
    return
  }

  const h1 = fallbackH1 || document.createElement('h1')
  h1.id = fallbackId
  h1.textContent = title
  h1.setAttribute(
    'style',
    'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;white-space:nowrap;',
  )

  if (!fallbackH1) {
    root.parentNode?.insertBefore(h1, root)
  }
}

const RouteTitleManager: React.FC = () => {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  const pageMeta = useMemo(() => {
    const meta = resolveRouteMeta(pathname, t)
    const brandTitle = t('seo.brand', { defaultValue: 'Frani' })
    const titleSuffix = t('seo.titleSuffix', { defaultValue: 'Compare and Buy Insurance Online' })
    return {
      ...meta,
      fullTitle: `${meta.title} | ${brandTitle} - ${titleSuffix}`,
    }
  }, [pathname, t])

  useEffect(() => {
    const applySeoMeta = () => {
      if (document.title !== pageMeta.fullTitle) {
        document.title = pageMeta.fullTitle
      }

      setOrUpdateMeta('meta[name="keywords"]', {
        name: 'keywords',
        content: pageMeta.title,
      })

      setOrUpdateMeta('meta[name="description"]', {
        name: 'description',
        content: pageMeta.description,
      })

      setOrUpdateMeta('meta[property="og:title"]', {
        property: 'og:title',
        content: pageMeta.fullTitle,
      })

      setOrUpdateMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: pageMeta.description,
      })

      setOrUpdateMeta('meta[name="twitter:title"]', {
        name: 'twitter:title',
        content: pageMeta.fullTitle,
      })

      setOrUpdateMeta('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: pageMeta.description,
      })

      setOrUpdateMeta('meta[name="robots"]', {
        name: 'robots',
        content: pageMeta.noindex ? 'noindex, nofollow' : 'index, follow',
      })

      setCanonicalLink(pathname)
      applyFallbackH1(pageMeta.title)
    }

    applySeoMeta()

    const headElement = document.head

    if (!headElement) {
      return
    }

    // Keep route-driven SEO metadata deterministic even when nested Helmet components update later.
    const observer = new MutationObserver(applySeoMeta)
    observer.observe(headElement, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [pageMeta, pathname])

  return null
}

export default RouteTitleManager
